import { type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { useState, useEffect, Suspense, lazy } from 'react'
import { BrandService } from '#app/entities/brand/service.server.js'
import { CarService } from '#app/entities/car/service.server.js'
import { CategoryService } from '#app/entities/category/service.server.js'
import { getMetaTags } from '#app/shared/lib/meta-tags.ts'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import Header from '#app/shared/ui/layout/Header.js'
import { CarBrandEnum, CategoryEnum } from '../../../../types/enums'
import Checked from './ui/Checked'
import Hero from './ui/Hero'
import PopularCategories from './ui/PopularCategories'

export const meta: MetaFunction = ({ matches }) => {
  return getMetaTags({
    matches,
    url: 'https://gamaoptima.com',
  })
}

const INITIAL_CARS_LIMIT = 4
const TOTAL_CARS_LIMIT = 8

export const COMMON_SELECT = {
  id: true,
  name: true,
  image: {
    select: {
      id: true,
    },
  },
} as const

const BRAND_NAMES = [
  CarBrandEnum.MERCEDES_BENZ,
  CarBrandEnum.BMW,
  CarBrandEnum.AUDI,
  CarBrandEnum.VOLVO,
  CarBrandEnum.PORSCHE,
  CarBrandEnum.LEXUS,
  CarBrandEnum.ALPINE,
  CarBrandEnum.ALFA_ROMEO,
  CarBrandEnum.JAGUAR,
  CarBrandEnum.VOLKSWAGEN,
  CarBrandEnum.MINI,
  CarBrandEnum.LAND_ROVER,
  CarBrandEnum.TOYOTA,
  CarBrandEnum.INFINITI,
] as const

const CATEGORY_NAMES = [
  CategoryEnum.SUV_4X4,
  CategoryEnum.COMPACTO,
  CategoryEnum.DEPORTIVO,
  CategoryEnum.BERLINA,
  CategoryEnum.COUPE,
  CategoryEnum.HATCHBACK,
  CategoryEnum.CABRIO,
] as const

export async function loader() {
  const categories = await CategoryService.findMany(COMMON_SELECT, {
    name: {
      in: [...CATEGORY_NAMES],
    },
  })
  const brands = await BrandService.findMany(COMMON_SELECT, {
    name: {
      in: [...BRAND_NAMES],
    },
  })
  const initialCars = await CarService.findMany({
    limit: TOTAL_CARS_LIMIT,
    orderBy: { CarScore: 'desc' },
    select: {
      id: true,
      images: {
        select: {
          id: true,
          isPreviewImage: true,
        },
      },
      description: true,
      model: true,
      price: true,
      discount: true,
      year: true,
      mileage: true,
      minFinancedAmount: true,
      minMonthlyPayment: true,
      horsePower: true,
      transmission: true,
      fuelType: true,
      slug: true,
      reservation: {
        select: {
          id: true,
          isPurchaseCompleted: true,
        },
      },
      brand: {
        select: {
          name: true,
        },
      },
    },
  })

  return {
    categories,
    brands,
    initialCars,
  }
}

type LoaderReturn = Awaited<ReturnType<typeof loader>>

interface MainContentProps extends LoaderReturn {
  categories: LoaderReturn['categories']
  brands: LoaderReturn['brands']
  initialCars: LoaderReturn['initialCars']
}

const CarDescription = lazy(
  () => import('#app/shared/ui/data-display/carDescription/CarDescription.js'),
)
const FinanciaciomHome = lazy(
  () => import('#app/shared/ui/layout/FinanciacionHome.tsx'),
)
const CarDisplayWithButtons = lazy(() => import('./ui/CarDisplayWithButtons'))
const CarBrands = lazy(() => import('./ui/CarBrands'))
const BottomHero = lazy(() => import('./ui/BottomHero'))

const MainContent = ({ categories, initialCars, brands }: MainContentProps) => {
  const [allCars, setAllCars] = useState(
    initialCars.slice(0, INITIAL_CARS_LIMIT),
  )
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setAllCars([...initialCars])
      setIsLoading(false)
    }, 1000)

    return () => clearTimeout(timer)
  }, [initialCars])

  return (
    <div
      className={`
        bg-neutral-25 px-4

        lg:px-4

        sm:px-14
      `}
    >
      <Spacer size="24px" sizeMd="56px" />
      <PopularCategories categories={categories} />
      <Spacer size="16px" sizeMd="80px" />
      <Checked />
      <Spacer size="24px" sizeMd="40px" />
      <Suspense fallback={''}>
        <CarDescription
          name="Destacados"
          cars={allCars as any}
          isLoading={isLoading}
        />
      </Suspense>
      <Spacer size="16px" sizeMd="40px" />
      <Suspense fallback={''}>
        <FinanciaciomHome />
      </Suspense>
      <Spacer sizeMd="40px" />
      <Spacer size="16px" />
      <Suspense fallback={''}>
        <CarDisplayWithButtons />
      </Suspense>
      <Spacer size="36px" sizeMd="64px" />
      <Suspense fallback={''}>
        <CarBrands brands={brands as any} />
      </Suspense>
      <Spacer size="48px" sizeMd="64px" />
      <Suspense fallback={''}>
        <BottomHero />
      </Suspense>
    </div>
  )
}

export default function Home() {
  const loaderData = useLoaderData<typeof loader>()

  return (
    <>
      <Header isSticky={true} showBanner={true} />

      <div
        className={`
          h-[530px] w-full flex-shrink-0 bg-bg-hero-mobile bg-cover bg-top
          bg-no-repeat

          lg:h-[820px]

          sm:h-[670px] sm:bg-bg-hero sm:bg-center
        `}
      >
        <Hero />
      </div>

      <MainContent {...(loaderData as any)} />
    </>
  )
}
